import {
  UpdatePresentationDocumentsReviewedBundleConfigurationInput,
  UpdatePresentationDocumentsReviewedDocumentInput,
} from '@/types/schema';

import { NEW_ROW_ID_SENTINEL } from '../../ClientPresentationDesigner.constants';
import {
  isEmptyKeyedListMutationOperations,
  mapKeyedListMutationOperations,
} from '../diff/mapKeyedListMutationOperations';
import {
  BundleConfigurationPayload,
  DiffBundleConfigurationArgs,
} from '../presentationSave.types';

function serverRowId(row: { id: string }): string | null {
  return row.id.startsWith(NEW_ROW_ID_SENTINEL) ? null : row.id;
}

/** Form array order is persisted as `sortOrder`. */
function positionsById(rows: { id: string }[]): Map<string, number> {
  return new Map(rows.map((row, index) => [row.id, index]));
}

function diffDisplayName(
  previous: string | null,
  next: string | null
): { displayName?: string; clearDisplayName?: boolean } | null {
  if (previous === next) return null;
  return next ? { displayName: next } : { clearDisplayName: true };
}

export function diffDocumentsReviewedBundleConfiguration({
  previous,
  next,
  context,
}: DiffBundleConfigurationArgs): BundleConfigurationPayload | null {
  const before = previous.documentsReviewedBundleConfiguration;
  const after = next.documentsReviewedBundleConfiguration;
  if (!before || !after?.id) return null;

  const update: UpdatePresentationDocumentsReviewedBundleConfigurationInput = {
    ...diffDisplayName(before.displayName, after.displayName),
  };
  const wasAt = positionsById(before.documents);

  const rows = mapKeyedListMutationOperations({
    previousItems: before.documents,
    currentItems: after.documents,
    getId: serverRowId,
    toCreate: (row, sortOrder) => ({
      create: {
        householdID: context.householdId,
        documentName: row.documentName,
        dateDisplay: row.dateDisplay,
        sortOrder,
      },
    }),
    toUpdate: ({ previous: was, next: is, sortOrder }) => {
      const row: UpdatePresentationDocumentsReviewedDocumentInput = {};
      if (was.documentName !== is.documentName) {
        row.documentName = is.documentName;
      }
      if (was.dateDisplay !== is.dateDisplay) {
        row.dateDisplay = is.dateDisplay;
      }
      if (wasAt.get(is.id) !== sortOrder) row.sortOrder = sortOrder;
      return Object.keys(row).length > 0 ? { id: is.id, update: row } : null;
    },
  });

  if (rows.removeIDs.length > 0) {
    update.removeReviewedDocumentIDs = rows.removeIDs;
  }
  if (
    Object.keys(update).length === 0 &&
    isEmptyKeyedListMutationOperations(rows)
  ) {
    return null;
  }

  return {
    updateDocumentsReviewedBundleConfiguration: {
      id: after.id,
      update,
      ...(rows.creates.length > 0
        ? { withReviewedDocuments: rows.creates }
        : {}),
      ...(rows.updates.length > 0
        ? { updateReviewedDocuments: rows.updates }
        : {}),
    },
  };
}
